import { CHARTERS, CLASSES, SETTLEMENTS, SETTLEMENT_IDS, SKILLS, SKILL_IDS } from "../content/core";
import { ANIMALS, BUILDINGS, PETS } from "../content/world";
import { CROP_BY_ID, CROPS, ITEMS, RECIPES, RARITY_MULTIPLIER } from "../content/items";
import { NPCS } from "../content/npcs";
import { MAIN_QUESTS, QUESTS } from "../content/quests";
import { pick, randomInt, stream } from "./rng";
import { syncObjectiveProgress } from "../systems/objectives";
import type {
  ActionEvent, BuildingDefinition, CharterId, ClassId, EquipmentSlot, FarmPlot, GameState, InventoryStack, ItemDefinition,
  NPCDefinition, PanelId, PetState, PlacedBuilding, QuestDefinition, Rarity, SettlementId, SkillId, SkillState, ToolMode, Weather,
} from "./types";

const VERSION = 1;
const SEASONS = ["Bloom", "Suncrest", "Harvest", "Frostfall"] as const;
const WEATHER: Weather[] = ["clear", "clear", "clear", "rain", "mist", "storm", "heat", "snow", "starfall"];
const clone = <T,>(value: T): T => structuredClone(value);
const uid = (state: Pick<GameState, "seed" | "rngCounter">, prefix: string): string => `${prefix}-${state.seed}-${state.rngCounter++}`;

export function xpForLevel(level: number): number { return 80 * level * level + 120 * level; }

function initialSkills(classId: ClassId): Record<SkillId, SkillState> {
  const cls = CLASSES[classId];
  return Object.fromEntries(SKILL_IDS.map((id) => [id, { level: Math.max(0, cls.skillBonuses[id] ?? 0), xp: 0, perks: [] }])) as unknown as Record<SkillId, SkillState>;
}

function initialFarm(): FarmPlot[] {
  const plots: FarmPlot[] = [];
  for (let y = 0; y < 10; y += 1) for (let x = 0; x < 12; x += 1) plots.push({ id: `plot-${x}-${y}`, x, y, tilled: false, watered: false, waterStreak: 0, growth: 0, ready: false, qualityBoost: 0 });
  return plots;
}

function initialQuests(): GameState["quests"] {
  const result: GameState["quests"] = {};
  for (const quest of Object.values(QUESTS)) result[quest.id] = { questId: quest.id, status: quest.id === "main-001" ? "active" : "locked", progress: quest.objectives.map(() => quest.id === "main-001" ? 1 : 0), acceptedDay: quest.id === "main-001" ? 1 : undefined };
  return result;
}

function baseInventory(state: GameState): void {
  const defaults: Record<string, number> = { "rusted-hoe": 1, "copper-pick": 1, wood: 45, stone: 30, fiber: 20, coal: 5, "turnip-seed": 10, "field-ration": 3 };
  for (const [itemId, quantity] of Object.entries(defaults)) addItemMutable(state, itemId, quantity);
  for (const itemId of CLASSES[state.classId].starterItems) addItemMutable(state, itemId, itemId.endsWith("-seed") || itemId === "reed-arrow" ? 12 : 1);
  for (const stack of state.inventory) {
    const def = ITEMS[stack.itemId];
    if (def?.slot && !state.equipment[def.slot]) state.equipment[def.slot] = { ...stack, quantity: 1 };
  }
  state.hotbar = ["rusted-hoe", "copper-pick", state.selectedSeedId, "field-ration", null, null, null, null];
}

export function createGame(classId: ClassId, playerName: string, seed: string, settings?: GameState["settings"]): GameState {
  const cls = CLASSES[classId];
  const state: GameState = {
    version: VERSION,
    seed,
    rngCounter: 1,
    scene: "world",
    panel: null,
    classId,
    playerName: playerName.trim() || "Rowan",
    day: 1,
    minute: 390,
    season: "Bloom",
    year: 1,
    weather: "clear",
    location: "lantern-acre",
    discoveredSettlements: ["lantern-acre", "fernhollow"],
    position: { x: 17, y: 17, direction: "down", moving: false, frame: 0 },
    stats: { health: cls.base.health, maxHealth: cls.base.health, stamina: cls.base.stamina, maxStamina: cls.base.stamina, mana: cls.base.mana, maxMana: cls.base.mana, armor: cls.base.armor, speed: cls.base.speed, hunger: 100, luck: classId === "shadeweaver" ? 5 : 0 },
    crowns: 450,
    guildSeals: 0,
    civicMarks: 0,
    relicDust: 0,
    skills: initialSkills(classId),
    inventory: [],
    equipment: {},
    hotbar: Array(8).fill(null),
    selectedHotbar: 0,
    selectedTool: "hand",
    selectedSeedId: classId === "arcanist" ? "moonbean-seed" : classId === "ranger" ? "berryseed-seed" : classId === "greenbinder" ? "sunroot-seed" : "turnip-seed",
    farm: initialFarm(),
    buildings: [{ uid: "starter-cabin", buildingId: "cabin", x: 2, y: 2, tier: 1 }],
    animals: [],
    npcs: Object.fromEntries(Object.keys(NPCS).map((id) => [id, { npcId: id, friendship: 0, talkedDay: 0, giftsThisWeek: 0, discovered: NPCS[id].settlement === "fernhollow" }])),
    quests: initialQuests(),
    activeQuestId: "main-001",
    reputation: { "lantern-acre": 50, fernhollow: 20 },
    pets: [],
    workers: [],
    market: { day: 1, sold7d: {}, multipliers: {} },
    lootPity: {},
    uniqueDrops: [],
    flags: { tutorialComplete: false, codexSeen: 0, totalSales: 0, seasonalCore: 0 },
    settings: settings ?? { sound: true, music: true, reducedMotion: false, largeText: false, screenShake: true, showGrid: true, difficulty: "standard" },
    gameStats: { cropsHarvested: 0, plotsWatered: 0, blocksMined: 0, enemiesDefeated: 0, itemsCrafted: 0, buildingsPlaced: 0, fishCaught: 0, giftsGiven: 0, questsCompleted: 0, bossesDefeated: 0, crownsEarned: 0, crownsSpent: 0, distanceTraveled: 0, daysPlayed: 0, startedAt: new Date().toISOString() },
    message: "Lantern Acre is yours. The key was exactly where Maelin promised.",
    log: ["You arrive at Lantern Acre beneath a clear Bloom morning."],
  };
  baseInventory(state);
  return refreshState(state);
}

function addItemMutable(state: GameState, itemId: string, quantity: number, rarity?: Rarity, quality?: number): void {
  if (!ITEMS[itemId] || quantity <= 0) return;
  const def = ITEMS[itemId];
  if (def.stack > 1 && rarity === undefined && quality === undefined) {
    const existing = state.inventory.find((stack) => stack.itemId === itemId && stack.rarity === undefined && stack.quality === undefined);
    if (existing) { existing.quantity += quantity; return; }
  }
  state.inventory.push({ itemId, quantity, rarity, quality, durability: def.stack === 1 ? 100 : undefined, uid: def.stack === 1 ? uid(state, "item") : undefined });
}

export function addItem(input: GameState, itemId: string, quantity: number, rarity?: Rarity, quality?: number): GameState {
  const state = clone(input);
  addItemMutable(state, itemId, quantity, rarity, quality);
  return applyActionMutable(state, { type: "collect", target: itemId, amount: quantity });
}

function removeItemMutable(state: GameState, itemId: string, quantity: number): boolean {
  let remaining = quantity;
  for (let index = state.inventory.length - 1; index >= 0 && remaining > 0; index -= 1) {
    const stack = state.inventory[index];
    if (stack.itemId !== itemId) continue;
    const taken = Math.min(stack.quantity, remaining);
    stack.quantity -= taken;
    remaining -= taken;
    if (stack.quantity <= 0) state.inventory.splice(index, 1);
  }
  return remaining === 0;
}

export function itemCount(state: GameState, itemId: string): number { return state.inventory.filter((stack) => stack.itemId === itemId).reduce((sum, stack) => sum + stack.quantity, 0); }
export function hasItems(state: GameState, ingredients: Record<string, number>): boolean { return Object.entries(ingredients).every(([id, amount]) => itemCount(state, id) >= amount); }

export function gainSkillMutable(state: GameState, skillId: SkillId, baseXp: number): void {
  if (baseXp <= 0) return;
  const cls = CLASSES[state.classId];
  const classMod = 1 + Math.max(-.25, Math.min(.35, (cls.skillBonuses[skillId] ?? 0) * .06 + (cls.skillPenalties[skillId] ?? 0) * .06));
  const charterMod = state.charterId ? 1 + (CHARTERS[state.charterId].xpBonuses[skillId] ?? 0) : 1;
  const skill = state.skills[skillId];
  skill.xp += Math.max(1, Math.floor(baseXp * classMod * charterMod));
  while (skill.level < 20 && skill.xp >= xpForLevel(skill.level + 1)) {
    skill.level += 1;
    state.log.unshift(`${SKILLS[skillId].name} reached level ${skill.level}.`);
    state.message = `${SKILLS[skillId].name} level ${skill.level}: ${SKILLS[skillId].automaticGain}`;
    if ([5, 10, 15, 20].includes(skill.level)) {
      const perkIndex = skill.level / 5 - 1;
      skill.perks.push(SKILLS[skillId].perkNames[perkIndex]);
      state.log.unshift(`Perk learned: ${SKILLS[skillId].perkNames[perkIndex]}.`);
    }
  }
}

export function gainSkill(input: GameState, skillId: SkillId, xp: number): GameState { const state = clone(input); gainSkillMutable(state, skillId, xp); return refreshState(state); }

function objectiveMatches(event: ActionEvent, objective: QuestDefinition["objectives"][number]): boolean {
  if (event.type !== objective.type) return false;
  if (event.target === objective.target) return true;
  const generic: Record<string, (e: ActionEvent) => boolean> = {
    "any-fish": (e) => e.type === "fish",
    "crowns": (e) => e.type === "earn",
    "regional-goods": (e) => e.type === "collect" && Boolean(ITEMS[e.target]),
    "regional-food": (e) => e.type === "collect" && ["food", "crop", "fish", "artisan", "animal"].includes(ITEMS[e.target]?.category ?? ""),
    "museum-items": (e) => e.type === "collect" && Boolean(ITEMS[e.target]),
    "quality-crop": (e) => e.type === "harvest" && e.amount > 0,
    "royal-quality": (e) => e.type === "harvest" && e.amount > 0,
    "elite-families": (e) => e.type === "defeat" && e.target.includes("elite"),
    "specialist-buildings": (e) => e.type === "build",
    "charter-profit": (e) => e.type === "earn",
    "codex-completion": (e) => e.type === "collect" || e.type === "explore",
    "all-settlements": (e) => e.type === "friendship",
    "charter-skill": (e) => e.type === "skill",
    mastery: (e) => e.type === "skill",
    "six-road-council": (e) => e.type === "talk",
    "final-choice": (e) => e.type === "talk",
    "named-pets": (e) => e.type === "pet",
    affinity: (e) => e.type === "pet",
    level: (e) => e.type === "pet",
    "cabin-tier-4": (e) => e.type === "build" && e.target === "cabin",
  };
  return generic[objective.target]?.(event) ?? false;
}

function applyActionMutable(state: GameState, event: ActionEvent): GameState {
  if (event.skill && event.xp) gainSkillMutable(state, event.skill, event.xp);
  for (const [questId, progress] of Object.entries(state.quests)) {
    if (progress.status !== "active") continue;
    const quest = QUESTS[questId];
    quest.objectives.forEach((objective, index) => {
      if (objectiveMatches(event, objective)) progress.progress[index] = Math.min(objective.amount, progress.progress[index] + event.amount);
    });
  }
  return refreshState(state);
}

export function applyAction(input: GameState, event: ActionEvent): GameState { return applyActionMutable(clone(input), event); }

function prerequisitesComplete(state: GameState, quest: QuestDefinition): boolean { return quest.prerequisites.every((id) => state.quests[id]?.status === "complete"); }
function questReady(state: GameState, quest: QuestDefinition): boolean { const p = state.quests[quest.id]; return p.status === "active" && quest.objectives.every((objective, index) => p.progress[index] >= objective.amount); }

function refreshQuestAvailabilityMutable(state: GameState): void {
  for (const quest of Object.values(QUESTS)) {
    const progress = state.quests[quest.id];
    if (progress.status === "locked" && prerequisitesComplete(state, quest)) progress.status = "available";
  }
}

function refreshUnlocksMutable(state: GameState): void {
  const completedMain = MAIN_QUESTS.filter((quest) => state.quests[quest.id]?.status === "complete").length;
  for (const settlement of SETTLEMENT_IDS) if (SETTLEMENTS[settlement].unlockQuest <= completedMain && !state.discoveredSettlements.includes(settlement)) state.discoveredSettlements.push(settlement);
}

export function refreshState(input: GameState): GameState {
  const state = input;
  syncObjectiveProgress(state);
  refreshQuestAvailabilityMutable(state);
  refreshUnlocksMutable(state);
  state.stats.health = Math.min(state.stats.health, state.stats.maxHealth);
  state.stats.stamina = Math.min(state.stats.stamina, state.stats.maxStamina);
  state.stats.mana = Math.min(state.stats.mana, state.stats.maxMana);
  return state;
}

export function acceptQuest(input: GameState, questId: string): GameState {
  const state = clone(input);
  const progress = state.quests[questId];
  if (!progress || progress.status !== "available") return state;
  progress.status = "active";
  progress.acceptedDay = state.day;
  state.activeQuestId = questId;
  state.message = `Quest accepted: ${QUESTS[questId].title}`;
  return refreshState(state);
}

export function completeQuest(input: GameState, questId: string): GameState {
  const state = clone(input);
  const quest = QUESTS[questId];
  const progress = state.quests[questId];
  if (!quest || !progress || !questReady(state, quest)) return state;
  progress.status = "complete";
  progress.completedDay = state.day;
  state.crowns += quest.rewards.crowns;
  state.gameStats.crownsEarned += quest.rewards.crowns;
  state.gameStats.questsCompleted += 1;
  if (quest.type === "main" && quest.index % 10 === 0) state.civicMarks += 2;
  if (quest.type === "side") state.guildSeals += 1;
  for (const [itemId, amount] of Object.entries(quest.rewards.items ?? {})) addItemMutable(state, itemId, amount);
  for (const [npcId, amount] of Object.entries(quest.rewards.friendship ?? {})) if (state.npcs[npcId]) state.npcs[npcId].friendship += amount;
  for (const [settlement, amount] of Object.entries(quest.rewards.reputation ?? {})) state.reputation[settlement as SettlementId] = (state.reputation[settlement as SettlementId] ?? 0) + amount;
  for (const [skill, xp] of Object.entries(quest.rewards.xp ?? {})) gainSkillMutable(state, skill as SkillId, xp);
  for (const flag of quest.rewards.flags ?? []) state.flags[flag] = true;
  state.log.unshift(`Completed: ${quest.title}.`);
  state.message = `${quest.title} complete. +${quest.rewards.crowns} crowns.`;
  if (questId === "main-100") state.scene = "ending";
  refreshQuestAvailabilityMutable(state);
  refreshUnlocksMutable(state);
  const nextMain = MAIN_QUESTS.find((entry) => state.quests[entry.id].status === "available");
  if (quest.type === "main" && nextMain) {
    state.quests[nextMain.id].status = "active";
    state.quests[nextMain.id].acceptedDay = state.day;
    state.activeQuestId = nextMain.id;
  }
  return refreshState(state);
}

export function chooseCharter(input: GameState, charterId: CharterId): GameState {
  const state = clone(input);
  if (state.charterId) return state;
  state.charterId = charterId;
  state.message = `${CHARTERS[charterId].name} signed. Its labor rules are permanent.`;
  return applyActionMutable(state, { type: "choose-charter", target: "charter", amount: 1 });
}

export function setPanel(input: GameState, panel: PanelId): GameState { const state = clone(input); state.panel = panel; return state; }
export function setTool(input: GameState, tool: ToolMode, seedId?: string): GameState { const state = clone(input); state.selectedTool = tool; if (seedId) state.selectedSeedId = seedId; return state; }

function consumeStamina(state: GameState, amount: number): boolean {
  if (state.stats.stamina < amount) { state.message = "You are too tired. Eat or sleep before doing more work."; return false; }
  state.stats.stamina -= amount;
  return true;
}

export function farmAction(input: GameState, plotId: string, mode: ToolMode = input.selectedTool): GameState {
  const state = clone(input);
  const plot = state.farm.find((entry) => entry.id === plotId);
  if (!plot) return state;
  const farmingLevel = state.skills.farming.level;
  if (mode === "hoe") {
    if (plot.tilled || !consumeStamina(state, Math.max(1, 4 - farmingLevel * .08))) return state;
    plot.tilled = true;
    state.message = "The soil turns dark and workable.";
    return applyActionMutable(state, { type: "plant", target: "tilled-soil", amount: 1, skill: "farming", xp: 3 });
  }
  if (mode === "water") {
    if (!plot.tilled || plot.watered || !consumeStamina(state, Math.max(1, 3 - farmingLevel * .06))) return state;
    plot.watered = true;
    state.gameStats.plotsWatered += 1;
    state.message = "The soil drinks deeply.";
    return applyActionMutable(state, { type: "plant", target: "watered-soil", amount: 1, skill: "farming", xp: plot.cropId ? 2 : 1 });
  }
  if (mode === "seed") {
    const crop = CROP_BY_ID[ITEMS[state.selectedSeedId]?.cropId ?? ""];
    if (!crop || !plot.tilled || plot.cropId || itemCount(state, crop.seedItemId) < 1) { state.message = "Till empty soil and choose an available seed first."; return state; }
    const classSeed = CLASSES[state.classId].starterItems.includes(crop.seedItemId);
    if (!classSeed && state.skills.farming.level < crop.skillLevel) { state.message = ` requires Farming .`; return state; }
    const greenhouse = state.buildings.some((building) => BUILDINGS[building.buildingId].tags.includes("climate"));
    if (!crop.seasons.includes(state.season) && !greenhouse) { state.message = `${crop.name} cannot grow in ${state.season} without a greenhouse.`; return state; }
    removeItemMutable(state, crop.seedItemId, 1);
    plot.cropId = crop.id;
    plot.plantedDay = state.day;
    plot.growth = 0;
    plot.ready = false;
    plot.waterStreak = 0;
    plot.qualityBoost = state.classId === "greenbinder" ? 5 : 0;
    state.message = `${crop.name} planted.`;
    return applyActionMutable(state, { type: "plant", target: crop.id, amount: 1, skill: "farming", xp: 4 });
  }
  if (mode === "harvest" || mode === "hand") {
    if (!plot.cropId || !plot.ready) { state.message = "Nothing here is ready to harvest."; return state; }
    const crop = CROP_BY_ID[plot.cropId];
    const rng = stream(state.seed, `harvest-${state.day}-${plot.id}-${state.rngCounter++}`);
    const classBonus = state.classId === "greenbinder" ? .08 : 0;
    const charterBonus = state.charterId === "homesteader" ? .08 : 0;
    const qualityChance = Math.min(.75, .05 + farmingLevel * .025 + plot.waterStreak * .012 + plot.qualityBoost / 100 + classBonus + charterBonus);
    let quality = 0;
    let roll = rng();
    while (roll < qualityChance / Math.max(1, quality + 1) && quality < 4) { quality += 1; roll = rng(); }
    const yieldBonus = rng() < .1 + farmingLevel * .01 ? 1 : 0;
    const amount = crop.baseYield + yieldBonus;
    addItemMutable(state, crop.cropItemId, amount, undefined, quality);
    state.gameStats.cropsHarvested += amount;
    state.message = `Harvested ${amount} ${crop.name}${quality ? ` at quality ${quality}` : ""}.`;
    if (crop.regrowDays) { plot.growth = Math.max(0, crop.growthDays - crop.regrowDays); plot.ready = false; plot.waterStreak = 0; } else Object.assign(plot, { cropId: undefined, plantedDay: undefined, growth: 0, ready: false, waterStreak: 0, watered: false });
    return applyActionMutable(state, { type: "harvest", target: crop.id, amount, skill: "farming", xp: 8 + crop.growthDays + quality * 3 });
  }
  return state;
}

export function forage(input: GameState, resource: "wood" | "stone" | "fiber" | "wild"): GameState {
  const state = clone(input);
  const rng = stream(state.seed, `forage-${state.day}-${state.minute}-${state.rngCounter++}`);
  const itemId = resource === "wild" ? pick(["silver-sage", "berryseed", "fiber", "honey"], rng) : resource;
  const amount = resource === "wood" ? randomInt(3, 7, rng) : resource === "stone" ? randomInt(2, 5, rng) : randomInt(1, 4, rng);
  if (!consumeStamina(state, resource === "wood" ? 6 : resource === "stone" ? 5 : 2)) return state;
  addItemMutable(state, itemId, amount);
  state.message = `Gathered ${amount} ${ITEMS[itemId]?.name ?? itemId}.`;
  return applyActionMutable(state, { type: "collect", target: itemId, amount, skill: "foraging", xp: 4 + amount * 2 });
}

export function fish(input: GameState): GameState {
  const state = clone(input);
  if (!consumeStamina(state, 5)) return state;
  const pools: Record<SettlementId, string[]> = {
    "lantern-acre": ["reed-darter", "moss-carp", "lantern-bass"],
    fernhollow: ["reed-darter", "moss-carp", "lantern-bass"],
    ironvale: ["snow-pike", "reed-darter", "silverfin"],
    tidecross: ["tide-mackerel", "silverfin", "glass-shrimp", "storm-ray"],
    emberport: ["ember-koi", "tide-mackerel", "silverfin"],
    glassmere: ["mirror-eel", "glass-shrimp", "star-jelly"],
    auricourt: ["crown-sturgeon", "silverfin", "mirror-eel"],
  };
  const rng = stream(state.seed, `fish-${state.location}-${state.day}-${state.rngCounter++}`);
  const pool = pools[state.location];
  let fishId = pick(pool, rng);
  if (rng() < .08 + state.skills.fishing.level * .01 + (state.weather === "storm" ? .12 : 0)) fishId = pool[pool.length - 1];
  addItemMutable(state, fishId, 1);
  state.minute += 35;
  state.gameStats.fishCaught += 1;
  state.message = `Caught ${ITEMS[fishId].name}.`;
  return applyActionMutable(state, { type: "fish", target: fishId, amount: 1, skill: "fishing", xp: 12 + Math.floor(ITEMS[fishId].baseValue / 35) });
}

export function buyAnimal(input: GameState, animalId: string): GameState {
  const state = clone(input);
  const animal = ANIMALS[animalId];
  if (!animal) return state;
  const hasShelter = state.buildings.some((building) => BUILDINGS[building.buildingId].tags.includes(animal.buildingTag));
  if (!hasShelter) { state.message = `${animal.name} needs a ${animal.buildingTag} first.`; return state; }
  if (state.crowns < animal.buyPrice) { state.message = `You need ${animal.buyPrice} crowns.`; return state; }
  state.crowns -= animal.buyPrice;
  state.gameStats.crownsSpent += animal.buyPrice;
  state.animals.push({ uid: uid(state, "animal"), animalId, name: `${animal.name} ${state.animals.length + 1}`, affection: 20, hunger: 80, health: 100, lastProductDay: state.day });
  state.message = `${animal.name} moved into Lantern Acre.`;
  return applyActionMutable(state, { type: "pet", target: animalId, amount: 1, skill: "petcraft", xp: 18 });
}

export function tendAnimal(input: GameState, animalUid: string): GameState {
  const state = clone(input);
  const animal = state.animals.find((entry) => entry.uid === animalUid);
  if (!animal || !consumeStamina(state, 3)) return state;
  animal.hunger = Math.min(100, animal.hunger + 35);
  animal.affection = Math.min(1000, animal.affection + 14 + state.skills.petcraft.level);
  animal.health = Math.min(100, animal.health + 8);
  state.message = `${animal.name} is fed, brushed, and content.`;
  return applyActionMutable(state, { type: "pet", target: animal.animalId, amount: 1, skill: "petcraft", xp: 7 });
}

export function collectAnimalProduct(input: GameState, animalUid: string): GameState {
  const state = clone(input);
  const owned = state.animals.find((entry) => entry.uid === animalUid);
  if (!owned) return state;
  const animal = ANIMALS[owned.animalId];
  if (state.day - owned.lastProductDay < animal.productDays || owned.hunger < 20 || owned.health < 40) {
    state.message = `${owned.name} has nothing ready yet.`;
    return state;
  }
  const quantity = owned.affection >= 500 ? 2 : 1;
  addItemMutable(state, animal.productId, quantity, undefined, owned.affection >= 700 ? 2 : owned.affection >= 300 ? 1 : 0);
  owned.lastProductDay = state.day;
  state.message = `Collected ${quantity} ${ITEMS[animal.productId].name}.`;
  return applyActionMutable(state, { type: "collect", target: animal.productId, amount: quantity, skill: "farming", xp: 10 + quantity * 4 });
}

function advanceAnimalsMutable(state: GameState): void {
  for (const animal of state.animals) {
    animal.hunger = Math.max(0, animal.hunger - 18);
    if (animal.hunger === 0) animal.health = Math.max(1, animal.health - 10);
    else if (animal.hunger > 50) animal.health = Math.min(100, animal.health + 2);
  }
}

function dayWeather(state: GameState): Weather {
  const rng = stream(state.seed, `weather-${state.year}-${state.season}-${state.day}`);
  let options = WEATHER;
  if (state.season === "Frostfall") options = ["clear", "snow", "snow", "mist", "starfall", "storm"];
  if (state.season === "Suncrest") options = ["clear", "clear", "heat", "heat", "storm", "rain"];
  return pick(options, rng);
}

function advanceCropsMutable(state: GameState): void {
  const greenhouse = state.buildings.some((building) => BUILDINGS[building.buildingId].tags.includes("climate"));
  for (const plot of state.farm) {
    if (!plot.cropId) { plot.watered = false; continue; }
    const crop = CROP_BY_ID[plot.cropId];
    if (!crop.seasons.includes(state.season) && !greenhouse) {
      Object.assign(plot, { cropId: undefined, growth: 0, ready: false, waterStreak: 0, watered: false });
      continue;
    }
    const naturallyWet = ["rain", "storm", "snow"].includes(state.weather);
    if (plot.watered || naturallyWet) { plot.growth += 1; plot.waterStreak += 1; } else plot.waterStreak = Math.max(0, plot.waterStreak - 1);
    plot.ready = plot.growth >= crop.growthDays;
    plot.watered = false;
  }
}

function payWorkersMutable(state: GameState): void {
  for (const worker of state.workers) {
    if (state.crowns < worker.wage) { worker.morale = Math.max(0, worker.morale - 15); continue; }
    state.crowns -= worker.wage;
    state.gameStats.crownsSpent += worker.wage;
    if (worker.type === "farm") {
      const efficiency = state.charterId ? CHARTERS[state.charterId].farmEfficiency : 0;
      const plots = state.farm.filter((plot) => plot.cropId && !plot.watered).slice(0, Math.max(1, Math.floor(12 * efficiency)));
      for (const plot of plots) plot.watered = true;
    }
    if (worker.type === "adventure") {
      const efficiency = state.charterId ? CHARTERS[state.charterId].adventureEfficiency : 0;
      const rng = stream(state.seed, `worker-${worker.uid}-${state.day}`);
      const material = pick(["stone", "wood", "copper-ore", "iron-ore", "fiber"], rng);
      addItemMutable(state, material, Math.max(4, Math.floor((5 + worker.level * 1.5) * efficiency)));
    }
    worker.morale = Math.min(100, worker.morale + 1);
  }
}

function updateMarketMutable(state: GameState): void {
  const rng = stream(state.seed, `market-${state.year}-${state.season}-${state.day}`);
  for (const item of Object.values(ITEMS)) {
    const history = state.market.sold7d[item.id] ?? [];
    const saturation = history.reduce((sum, amount) => sum + amount, 0);
    const noise = .92 + rng() * .18;
    state.market.multipliers[item.id] = Math.max(.65, Math.min(1.45, noise - Math.min(.35, saturation / 180)));
    state.market.sold7d[item.id] = [...history, 0].slice(-7);
  }
  state.market.day = state.day;
}

export function sleep(input: GameState): GameState {
  const state = clone(input);
  advanceCropsMutable(state);
  payWorkersMutable(state);
  advanceAnimalsMutable(state);
  state.day += 1;
  state.gameStats.daysPlayed += 1;
  if ((state.day - 1) % 28 === 0 && state.day > 1) {
    const current = SEASONS.indexOf(state.season);
    if (current === SEASONS.length - 1) state.year += 1;
    state.season = SEASONS[(current + 1) % SEASONS.length];
  }
  state.minute = 360;
  state.weather = dayWeather(state);
  state.stats.health = state.stats.maxHealth;
  state.stats.stamina = state.stats.maxStamina;
  state.stats.mana = state.stats.maxMana;
  state.stats.hunger = 100;
  updateMarketMutable(state);
  for (const npc of Object.values(state.npcs)) { npc.giftsThisWeek = state.day % 7 === 1 ? 0 : npc.giftsThisWeek; }
  state.message = `${state.season} ${((state.day - 1) % 28) + 1}, Year ${state.year}. ${state.weather[0].toUpperCase()}${state.weather.slice(1)} weather.`;
  state.log.unshift(`Day ${state.day} begins with ${state.weather}.`);
  return applyActionMutable(state, { type: "sleep", target: "day", amount: 1 });
}

export function advanceTime(input: GameState, minutes: number): GameState {
  let state = clone(input);
  state.minute += minutes;
  state.stats.hunger = Math.max(0, state.stats.hunger - minutes / 240);
  if (state.minute >= 1560) state = sleep(state);
  return state;
}

export function travel(input: GameState, settlement: SettlementId): GameState {
  const state = clone(input);
  if (!state.discoveredSettlements.includes(settlement)) { state.message = "That road has not opened yet."; return state; }
  if (state.location === settlement) return state;
  state.location = settlement;
  state.position = { x: 20, y: 20, direction: "down", moving: false, frame: 0 };
  state.minute += 35;
  state.gameStats.distanceTraveled += 1;
  for (const npc of Object.values(state.npcs)) if (NPCS[npc.npcId].settlement === settlement) npc.discovered = true;
  state.message = `Arrived at ${SETTLEMENTS[settlement].name}.`;
  return applyActionMutable(state, { type: "travel", target: settlement, amount: 1, settlement });
}

export function getSellPrice(state: GameState, stack: InventoryStack): number {
  const item = ITEMS[stack.itemId];
  if (!item || item.baseValue <= 0) return 0;
  const demand = SETTLEMENTS[state.location].demand[item.category] ?? 1;
  const market = state.market.multipliers[item.id] ?? 1;
  const quality = 1 + (stack.quality ?? 0) * .18;
  const rarity = RARITY_MULTIPLIER[stack.rarity ?? item.rarity];
  const social = 1 + state.skills.social.level * .004;
  return Math.max(1, Math.floor(item.baseValue * demand * market * quality * rarity * social));
}

export function getBuyPrice(state: GameState, itemId: string): number {
  const item = ITEMS[itemId];
  if (!item) return 999999;
  const socialDiscount = Math.min(.18, state.skills.social.level * .004);
  return Math.max(1, Math.ceil(item.baseValue * (item.category === "seed" ? 1 : 1.45) * (1 - socialDiscount)));
}

export function sellItem(input: GameState, itemId: string, quantity = 1): GameState {
  const state = clone(input);
  const available = itemCount(state, itemId);
  const amount = Math.min(quantity, available);
  if (amount <= 0) return state;
  const representative = state.inventory.find((stack) => stack.itemId === itemId)!;
  const each = getSellPrice(state, representative);
  removeItemMutable(state, itemId, amount);
  const earned = each * amount;
  state.crowns += earned;
  state.gameStats.crownsEarned += earned;
  state.flags.totalSales = Number(state.flags.totalSales ?? 0) + earned;
  const history = state.market.sold7d[itemId] ?? Array(7).fill(0);
  history[history.length - 1] += amount;
  state.market.sold7d[itemId] = history;
  state.message = `Sold ${amount} ${ITEMS[itemId].name} for ${earned} crowns.`;
  return applyActionMutable(state, { type: "earn", target: "crowns", amount: earned, skill: "social", xp: Math.max(1, Math.floor(earned / 20)) });
}

export function buyItem(input: GameState, itemId: string, quantity = 1): GameState {
  const state = clone(input);
  const price = getBuyPrice(state, itemId) * quantity;
  if (!ITEMS[itemId] || state.crowns < price) { state.message = "You cannot afford that purchase."; return state; }
  state.crowns -= price;
  state.gameStats.crownsSpent += price;
  addItemMutable(state, itemId, quantity);
  state.message = `Bought ${quantity} ${ITEMS[itemId].name}.`;
  return state;
}

export function craft(input: GameState, recipeId: string): GameState {
  const state = clone(input);
  const recipe = RECIPES[recipeId];
  if (!recipe) return state;
  if (state.skills[recipe.skill].level < recipe.skillLevel) { state.message = `${recipe.name} requires ${SKILLS[recipe.skill].name} ${recipe.skillLevel}.`; return state; }
  if (!hasItems(state, recipe.ingredients)) { state.message = "Missing recipe ingredients."; return state; }
  for (const [itemId, amount] of Object.entries(recipe.ingredients)) removeItemMutable(state, itemId, amount);
  const rng = stream(state.seed, `craft-${recipe.id}-${state.rngCounter++}`);
  const qualityChance = Math.min(.6, .04 + state.skills.crafting.level * .02 + (state.classId === "tinker" ? .08 : 0));
  let rarity: Rarity | undefined;
  if (rng() < qualityChance) rarity = rng() < .08 + state.skills.crafting.level * .005 ? "rare" : "fine";
  addItemMutable(state, recipe.output.itemId, recipe.output.quantity, rarity);
  state.minute += recipe.craftMinutes;
  state.gameStats.itemsCrafted += recipe.output.quantity;
  state.message = `Crafted ${recipe.output.quantity} ${ITEMS[recipe.output.itemId].name}.`;
  return applyActionMutable(state, { type: "craft", target: recipe.output.itemId, amount: recipe.output.quantity, skill: recipe.skill, xp: 10 + recipe.skillLevel * 6 });
}

function buildingPosition(state: GameState, definition: BuildingDefinition): { x: number; y: number } | null {
  for (let y = 1; y < 16; y += 1) for (let x = 1; x < 20; x += 1) {
    if (x >= 8 && x <= 19 && y >= 5 && y <= 14) continue;
    const overlap = state.buildings.some((placed) => {
      const other = BUILDINGS[placed.buildingId];
      return x < placed.x + other.width && x + definition.width > placed.x && y < placed.y + other.height && y + definition.height > placed.y;
    });
    if (!overlap) return { x, y };
  }
  return null;
}

export function construct(input: GameState, buildingId: string): GameState {
  const state = clone(input);
  const definition = BUILDINGS[buildingId];
  if (!definition) return state;
  if (state.skills.building.level < definition.skillLevel) { state.message = `${definition.name} requires Building ${definition.skillLevel}.`; return state; }
  const materialDiscount = Math.min(.15, state.skills.building.level * .01);
  const adjusted = Object.fromEntries(Object.entries(definition.cost).map(([id, amount]) => [id, Math.max(1, Math.ceil(amount * (1 - materialDiscount)))]));
  if (!hasItems(state, adjusted) || state.crowns < definition.crownCost) { state.message = "Missing construction materials or crowns."; return state; }
  const position = buildingPosition(state, definition);
  if (!position) { state.message = "No safe open construction plot remains."; return state; }
  for (const [itemId, amount] of Object.entries(adjusted)) removeItemMutable(state, itemId, amount);
  state.crowns -= definition.crownCost;
  state.gameStats.crownsSpent += definition.crownCost;
  state.buildings.push({ uid: uid(state, "building"), buildingId, x: position.x, y: position.y, tier: 1 });
  state.gameStats.buildingsPlaced += 1;
  state.message = `${definition.name} constructed.`;
  return applyActionMutable(state, { type: "build", target: buildingId, amount: 1, skill: "building", xp: 35 + definition.skillLevel * 12 });
}

export function upgradeBuilding(input: GameState, buildingUid: string): GameState {
  const state = clone(input);
  const placed = state.buildings.find((entry) => entry.uid === buildingUid);
  if (!placed) return state;
  const definition = BUILDINGS[placed.buildingId];
  if (placed.tier >= definition.maxTier) return state;
  const cost = definition.crownCost * (placed.tier + 1);
  if (state.crowns < cost) { state.message = `Upgrade requires ${cost} crowns.`; return state; }
  state.crowns -= cost;
  state.gameStats.crownsSpent += cost;
  placed.tier += 1;
  state.message = `${definition.name} upgraded to tier ${placed.tier}.`;
  return applyActionMutable(state, { type: "build", target: definition.id, amount: 1, skill: "building", xp: 60 * placed.tier });
}

export function talkToNpc(input: GameState, npcId: string): GameState {
  const state = clone(input);
  const npc = NPCS[npcId];
  const relationship = state.npcs[npcId];
  if (!npc || !relationship) return state;
  const firstToday = relationship.talkedDay !== state.day;
  if (firstToday) {
    const gain = Math.floor(18 * (1 + state.skills.social.level * .01));
    relationship.friendship += gain;
    relationship.talkedDay = state.day;
    gainSkillMutable(state, "social", 6);
  }
  const line = npc.dialogue[(state.day + relationship.friendship) % npc.dialogue.length];
  state.message = `${npc.name}: “${line}”${firstToday ? " Friendship increased." : ""}`;
  return applyActionMutable(state, { type: "talk", target: npcId, amount: 1 });
}

export function giftNpc(input: GameState, npcId: string, itemId: string): GameState {
  const state = clone(input);
  const npc = NPCS[npcId];
  const relationship = state.npcs[npcId];
  if (!npc || !relationship || itemCount(state, itemId) < 1 || relationship.giftsThisWeek >= 2) return state;
  removeItemMutable(state, itemId, 1);
  const base = npc.favorites.includes(itemId) ? 80 : npc.dislikes.includes(itemId) ? -20 : 25;
  const gain = Math.floor(base * (1 + state.skills.social.level * .01));
  relationship.friendship = Math.max(0, relationship.friendship + gain);
  relationship.giftsThisWeek += 1;
  state.gameStats.giftsGiven += 1;
  state.message = `${npc.name} ${gain > 50 ? "loves" : gain < 0 ? "dislikes" : "accepts"} the ${ITEMS[itemId]?.name ?? itemId}.`;
  return applyActionMutable(state, { type: "gift", target: npcId, amount: 1, skill: "social", xp: Math.max(1, Math.floor(Math.abs(gain) / 5)) });
}

export function hatchPet(input: GameState, petId: string, name?: string): GameState {
  const state = clone(input);
  const pet = PETS[petId];
  if (!pet || itemCount(state, pet.eggItemId) < 1) { state.message = "You do not have this companion's egg or token."; return state; }
  removeItemMutable(state, pet.eggItemId, 1);
  const instance: PetState = { uid: uid(state, "pet"), petId, name: name?.trim() || pet.name, level: 1, xp: 0, affinity: 0, active: state.pets.length === 0, colorVariant: state.rngCounter % 4 };
  state.pets.push(instance);
  if (instance.active) state.activePetUid = instance.uid;
  state.message = `${instance.name} joins you.`;
  return applyActionMutable(state, { type: "pet", target: "named-pets", amount: 1, skill: "petcraft", xp: 30 });
}

export function setActivePet(input: GameState, petUid: string): GameState {
  const state = clone(input);
  state.activePetUid = petUid;
  for (const pet of state.pets) pet.active = pet.uid === petUid;
  return state;
}

export function gainPetXpMutable(state: GameState, amount: number, relevantSkill?: SkillId): void {
  const pet = state.pets.find((entry) => entry.uid === state.activePetUid);
  if (!pet) return;
  const def = PETS[pet.petId];
  const affinityGain = relevantSkill === def.affinitySkill ? amount : Math.ceil(amount * .35);
  pet.xp += amount;
  pet.affinity += affinityGain;
  while (pet.level < 10 && pet.xp >= pet.level * pet.level * 45) {
    pet.level += 1;
    state.log.unshift(`${pet.name} reached level ${pet.level}.`);
    if (pet.level === 5 && !pet.temperament) pet.temperament = "clever";
  }
  gainSkillMutable(state, "petcraft", Math.max(1, Math.floor(affinityGain / 3)));
}

export function hireWorker(input: GameState, type: "farm" | "adventure" | "craft" | "courier"): GameState {
  const state = clone(input);
  if (!state.charterId) { state.message = "Choose a life charter before hiring permanent labor."; return state; }
  const charter = CHARTERS[state.charterId];
  if (type === "farm" && !charter.canHireFarm) { state.message = `${charter.name} forbids hired farm labor.`; return state; }
  if (type === "adventure" && !charter.canHireAdventure) { state.message = `${charter.name} requires personal expeditions.`; return state; }
  if (state.charterId === "guildmaster" && state.workers.some((worker) => worker.type === type)) { state.message = "Guildmaster Charter permits only one worker of this type."; return state; }
  const baseWage = type === "farm" ? 70 : type === "adventure" ? 80 : 75;
  const wage = Math.ceil(baseWage * (state.charterId === "guildmaster" ? 1.25 : 1));
  state.workers.push({ uid: uid(state, "worker"), name: `${type[0].toUpperCase()}${type.slice(1)} Hand ${state.workers.length + 1}`, type, wage, level: 1, assignment: "general", morale: 80 });
  state.message = `Hired a ${type} worker for ${wage} crowns per day.`;
  return state;
}

export function equipItem(input: GameState, stackUid: string): GameState {
  const state = clone(input);
  const stack = state.inventory.find((entry) => entry.uid === stackUid);
  if (!stack) return state;
  const item = ITEMS[stack.itemId];
  if (!item.slot) return state;
  const old = state.equipment[item.slot];
  state.equipment[item.slot] = { ...stack, quantity: 1 };
  if (old) addItemMutable(state, old.itemId, 1, old.rarity, old.quality);
  removeItemMutable(state, stack.itemId, 1);
  state.message = `${item.name} equipped.`;
  return state;
}

export function consumeItem(input: GameState, itemId: string): GameState {
  const state = clone(input);
  const item: ItemDefinition | undefined = ITEMS[itemId];
  if (!item || itemCount(state, itemId) < 1 || !["food", "consumable"].includes(item.category)) return state;
  removeItemMutable(state, itemId, 1);
  state.stats.health = Math.min(state.stats.maxHealth, state.stats.health + (item.stats?.health ?? 0));
  state.stats.stamina = Math.min(state.stats.maxStamina, state.stats.stamina + (item.stats?.stamina ?? 0));
  state.stats.mana = Math.min(state.stats.maxMana, state.stats.mana + (item.stats?.mana ?? 0));
  state.message = `Used ${item.name}.`;
  return state;
}

export function updatePosition(input: GameState, x: number, y: number, direction: GameState["position"]["direction"], moving: boolean, deltaDistance = 0): GameState {
  const state = clone(input);
  state.position = { x, y, direction, moving, frame: moving ? (state.position.frame + 1) % 4 : 0 };
  state.gameStats.distanceTraveled += deltaDistance;
  return state;
}

export function availableShopItems(state: GameState): string[] {
  const basics = CROPS.filter((crop) => crop.seasons.includes(state.season)).map((crop) => crop.seedItemId);
  if (state.location === "ironvale") basics.push("repair-kit", "reed-arrow", "iron-pick");
  if (state.location === "tidecross") basics.push("salt", "reed-arrow", "field-ration");
  if (state.location === "glassmere") basics.push("mana-tea", "glass", "starflower-seed");
  if (state.location === "auricourt") basics.push("cloth", "wine", "relic-fragment");
  return [...new Set(basics)].filter((id) => ITEMS[id]);
}

export function currentNpcSchedule(npc: NPCDefinition, minute: number) { return npc.schedule.find((entry) => minute >= entry.start && minute < entry.end) ?? npc.schedule[npc.schedule.length - 1]; }
export function npcsAtLocation(state: GameState): NPCDefinition[] { return Object.values(NPCS).filter((npc) => currentNpcSchedule(npc, state.minute).location === state.location); }
export function currentDayOfSeason(state: GameState): number { return ((state.day - 1) % 28) + 1; }
export function formatTime(minute: number): string { const normalized = minute % 1440; const hour = Math.floor(normalized / 60); const mins = Math.floor(normalized % 60); const suffix = hour >= 12 ? "PM" : "AM"; return `${hour % 12 || 12}:${String(mins).padStart(2, "0")} ${suffix}`; }
